<template>
  <div class="new-header" ref="domRefRoot">
    <div class="new-header__left-side">
      <!-- User info snippet -->
      <div v-if="globalAuth.isAuthenticated" class="user-info-snippet new-header__user-info-snippet" ref="domRefUserInfoSnippet">
        <img v-if="props.orgInContext?.logoUri" class="user-info-snippet__logo" :src="`${contentBaseUri}/cms/images/orgs/${props.orgInContext?.logoUri}`" :alt="props.orgInContext?.name" />

        <LinkWithIcon class="user-info-snippet__link" isNoUnderline icon-inset="2px auto auto 1px" @click="handleUserInfoSnippetClick">
          <span class="global-h5 hide-on-mobile" style="margin-right: 4px">{{ props.orgInContext?.name ?? "loading..." }}</span>
          <template v-if="isUserAdminOfCurrentOrg" #iconRight>
            <IconEmbedded
              :name="isUserMenuDropdownVisible ? 'caret-top_4' : 'caret-bottom_4'"
              :size="13"
              :style="isUserMenuDropdownVisible ? 'position: relative; top: 1px' : 'position: relative; top: 2px'"
            />
          </template>
        </LinkWithIcon>

        <teleport to="body">
          <UserMenuDropdown
            :class="{
              'user-info-snippet__user-menu-dropdown': true,
              'user-info-snippet__user-menu-dropdown--invisible': !isUserMenuDropdownVisible,
            }"
            :orgInContext="props.orgInContext"
            :style="{
              zIndex: 100000,
              top: userMenuDropdownPosition.y + 'px',
              left: userMenuDropdownPosition.x + 'px',
            }"
            v-click-outside="event => handleUserMenuDropdownClickOutside(event)"
            @requestClose="isUserMenuDropdownVisible = false"
          />
        </teleport>
      </div>
      <!-- / User info snippet -->
    </div>

    <div class="new-header__center">
      <!-- Shrpa logo -->
      <RouterLink class="shrpa-logo new-header__shrpa-logo" lang="en" hreflang="en" :to="{ name: 'Home' }">
        <img class="shrpa-logo__img" src="https://cdn.shrpa.com/images/shrpa-full-color.png" alt="Shrpa" />
      </RouterLink>
      <!-- / Shrpa logo -->
    </div>

    <div class="new-header__right-side">
      <!-- Additional links -->
      <ul class="additional-links new-header__additional-links">
        <template v-if="!globalAuth.isAuthenticated">
          <li class="additional-links__li">
            <a class="global-h5 additional-links__link" lang="en" hreflang="en" :href="globalGetLink('ForCreators')">For Creators</a>
          </li>

          <li class="additional-links__li">
            <SrpButton
              class="additional-links__button"
              :isDisabled="globalAuth.loading"
              @click="!globalAuth.loading && emit('requestToOpenLoginModal')"
              fill="outlined"
              size="small"
            >
              {{ globalAuth.loading ? "..." : "Log in" }}
            </SrpButton>
          </li>
        </template>

        <li v-else class="additional-links__li">
          <span v-if="!dashboardLink" class="global-h5 additional-links__link additional-links__link--disabled" aria-disabled="true">Dashboard</span>
          <RouterLink v-else class="global-h5 additional-links__link" :to="dashboardLink">Dashboard</RouterLink>
        </li>
      </ul>
      <!-- / Additional links -->

      <!-- Note: This class is used as a selector and setup in the Frill UI, so don't change it -->
      <LinkWithIcon v-if="props.orgInContext?.subType === 'Original'" class="large bell outline icon shrpa-whats-new hide-on-mobile new-header__bell-icon-button">
        <template #icon><IconEmbedded name="bell_2" :size="26" color="rgba(0, 0, 0, 0.5)" /></template>
      </LinkWithIcon>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch, Ref } from "vue";
import { PartnerNavInfo } from "@contracts/partnerNavInfo";
import findNearestParent from "@logic/dom/FindNearestParent";
import { useUserProfileStore } from "@stores/userProfileStore";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import UserMenuDropdown from "./UserMenuDropdown.vue";
import SrpButton from "@components/ui/SrpButton.vue";

// Stores
const userProfileStore = useUserProfileStore();

// Global variables
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;
const globalGetLink = inject("globalGetLink") as any;
const screenSizeUpdateKeyThrottled = inject("screenSizeUpdateKeyThrottled") as Ref<symbol>;

defineOptions({ name: "NewHeader" });

const props = withDefaults(
  defineProps<{
    orgInContext: PartnerNavInfo | null;
  }>(),
  {
    orgInContext: null,
  }
);

const emit = defineEmits<{
  (e: "requestToOpenLoginModal"): void;
}>();

const domRefRoot = ref<HTMLElement | null>(null);

// Toggles the user menu dropdown =============================================
const domRefUserInfoSnippet = ref<HTMLElement | null>(null);
const isUserMenuDropdownVisible = ref<boolean>(false);

function handleUserInfoSnippetClick() {
  if (isUserAdminOfCurrentOrg.value) {
    isUserMenuDropdownVisible.value = !isUserMenuDropdownVisible.value;
  } else if (props.orgInContext?.siteUri) {
    window.location.href = props.orgInContext.siteUri;
  }
}

function handleUserMenuDropdownClickOutside(event: Event) {
  // Do nothing if the user clicked on the user snippet link
  // (to prevent closing and immediate opening bug)
  const isClickedOnUserSnippet = Boolean(findNearestParent(event.target, [domRefUserInfoSnippet.value]));

  if (!isClickedOnUserSnippet) {
    isUserMenuDropdownVisible.value = false;
  }
}

// Generates dashboard link ===================================================
const dashboardLink = computed<any>(() => {
  if (userProfileStore.getViewingUserProfile?.adminOf?.length > 0) return { name: "CommunityAdminDashboard" };
  const creatorId = userProfileStore.getViewingUserProfile?.sherpaId;
  if (!creatorId) return null;
  return { name: "CreatorDashboard", params: { creatorId } };
});

// Check if user is admin of current org ======================================
const isUserAdminOfCurrentOrg = computed<boolean>(() => {
  if (!props.orgInContext?.customerId || !userProfileStore.getViewingUserProfile?.adminOf) return false;
  // Special check for superusers
  if (userProfileStore.isSuperOrSalesUser) {
    // Special special case for superusers coming from a partner site (mostly for demos)
    if (props.orgInContext.siteUri) return false;
    // Otherwise they have permission for everybody
    return true;
  }

  // For normal usersjust check the adminOf field
  return userProfileStore.getViewingUserProfile.adminOf.includes(props.orgInContext.customerId);
});

// Calc the user menu dropdown position =======================================
const userMenuDropdownPosition = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const userMenuDropdownTopIndent = -27;
const userMenuDropdownLeftIndent = -20;

watch(
  () => [isUserMenuDropdownVisible.value, screenSizeUpdateKeyThrottled.value],
  () => {
    const userInfoSnippetClientRect = domRefUserInfoSnippet.value.getBoundingClientRect();
    const headerHeight = domRefRoot.value?.getBoundingClientRect().height;

    userMenuDropdownPosition.value.y = userInfoSnippetClientRect.top + headerHeight + userMenuDropdownTopIndent;
    userMenuDropdownPosition.value.x = userInfoSnippetClientRect.left + userMenuDropdownLeftIndent;
  }
);
</script>

<style scoped lang="scss">
// User info snippet ==========================================================
.user-info-snippet {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 0;

  &__logo {
    max-width: 60px;
    max-height: 60px;
    margin-right: 12px;
  }

  &__link {
    &::after {
      content: "";
      width: calc(100% + 50px);
      height: calc(100% + 20px);
      position: absolute;
      inset: -10px auto auto -50px;
    }
  }

  &__user-menu-dropdown {
    position: absolute;
    transform: translateY(0);
    opacity: 1;
    transition:
      transform 0.05s ease-in-out,
      opacity 0.05s ease-in-out;

    &--invisible {
      transform: translateY(-10px);
      opacity: 0;
      pointer-events: none;
    }
  }
}

// Shrpa logo =================================================================
.shrpa-logo {
  width: 30px;
  height: 40px;
  display: flex;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transform: scale(1);
  transition: transform 0.05s ease-in-out;

  &:hover {
    transform: scale(1.1);
  }

  &__img {
    height: 100%;
  }
}

// Additional links ===========================================================
.additional-links {
  gap: 23px;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  list-style: none;

  &__li {
  }

  &__link {
    font-weight: 400;

    &:hover {
      color: rgba(5, 133, 135, 1);
      text-decoration: underline;
      text-decoration-color: rgba(5, 133, 135, 0.2);
      text-underline-offset: 3px;
      text-decoration-thickness: 1px;
    }

    &--disabled {
      opacity: 0.45;
      pointer-events: none;
      cursor: default;

      &:hover {
        color: inherit;
        text-decoration: none;
      }
    }
  }

  &__button {
  }
}

// New header =================================================================
.new-header {
  height: 75px;
  padding: 0 27px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 0;

  &::after {
    content: "";
    width: 100dvw;
    height: 0;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
    position: absolute;
    inset: auto auto 0 calc(50% - 50dvw);
    pointer-events: none;
  }

  &__left-side {
    width: 33.3%;
    display: flex;
    align-items: center;
  }

  &__center {
    width: 33.3%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__right-side {
    width: 33.3%;
    gap: 25px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  &__user-info-snippet {
  }

  &__shrpa-logo {
  }

  &__additional-links {
  }

  &__bell-icon-button {
    opacity: 0.8 !important;
  }
}
</style>
